import { AccommodationIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import React from 'react'
import {
	StayCity,
	StayDateChip,
	StayName,
	StayRow,
	StayRows,
	StayText,
	StayThumb,
} from './AccommodationsTile.styles'
import BentoTile from './BentoTile'
import { AccommodationsSection, TileState } from './core'
import EmptyState from './EmptyState'
import SectionTeaser from './SectionTeaser'

interface AccommodationsTileProps {
	section: AccommodationsSection
	/** Resolved tile state from the shared state machine (see `resolveTileState`). */
	state: TileState
	/** Navigate to the accommodations page. */
	onNavigate: () => void
	/** Render a skeleton while the accommodations query loads. */
	loading?: boolean
}

/**
 * The tall accommodations bento tile (spans two rows): up to three stay rows of
 * a 92×92 photo (warm icon-chip fallback), name, city caption, and a warm date
 * chip "check-in → check-out". Footer counts all stays.
 */
const AccommodationsTile: React.FC<AccommodationsTileProps> = ({ section, state, onNavigate, loading }) => {
	const icon = <AccommodationIcon sx={{ fontSize: 16 }} />
	const { count, items } = section

	if (state === 'teaser') {
		return <SectionTeaser icon={icon} label="Accommodations" count={count} colSpan={7} rowSpan={2} loading={loading} />
	}

	const body =
		state === 'detail' ? (
			<StayRows>
				{items.map(stay => (
					<StayRow key={stay.id}>
						<StayThumb
							url={stay.photoUrl}
							alt=""
							fallbackIcon={<AccommodationIcon sx={{ fontSize: 28 }} />}
						/>
						<StayText>
							<StayName>{stay.name}</StayName>
							{stay.city && <StayCity>{stay.city}</StayCity>}
							{stay.dateRange && <StayDateChip>{stay.dateRange}</StayDateChip>}
						</StayText>
					</StayRow>
				))}
			</StayRows>
		) : (
			<EmptyState
				icon={<AccommodationIcon sx={{ fontSize: 32 }} />}
				text="No stays yet — add hotels, Airbnbs, and stays for every night of the trip."
			/>
		)

	return (
		<BentoTile
			icon={icon}
			label="Accommodations"
			count={count > 0 ? count : undefined}
			footer={count > 0 ? `All ${countLabel(count, 'stay')}` : 'See all stays'}
			ariaLabel="View accommodations"
			colSpan={7}
			rowSpan={2}
			onClick={onNavigate}
			loading={loading}
			skeletonRows={4}
		>
			{body}
		</BentoTile>
	)
}

export default AccommodationsTile
